import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useFlag } from '@innovid/feature-flags-react';
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';
import {
	Modal,
	Button,
	ReduxControls,
	ProgressIndicator,
	EmptyItem
} from "rhombus-react";

import useActiveCreative from "hooks/useActiveCreative";

import {
	setVASTUploadList,
	uploadVASTCreative,
	encodeVASTCreatives,
	encodeVastGrowls,
	uploadVASTAfterEffectsCreative,
	encodeVASTAfterEffectsCreative,
	uploadVASTImposiumCreative,
	encodeVASTImposiumCreative
} from "reducers/creatives/VAST";
import { addNotification } from "reducers/Notifications";
import { loadCreatives } from "reducers/Creatives";
import { openModal } from "reducers/Modal";

import {
	MODAL_TYPES,
	UPLOAD_AFTER_EFFECTS_MAX_BYTES,
	UPLOAD_VAST_ACCEPTED_FORMATS,
	UPLOAD_VAST_AFTER_EFFECTS_FORMATS,
	UPLOAD_VAST_CREATIVE,
	UPLOAD_VIDEO_ASSET_MAX_BYTES,
	VAST_DYNAMIC_AFTER_EFFECTS,
	VAST_DYNAMIC,
	VAST_NON_DYNAMIC,
	VAST_IMPOSIUM,
	VAST_BUILD_METHOD_LABELS
} from "utils/constants";
import VASTUploadList from "./VASTUploadList";
import SkipModal from "./SkipModal";
import PartnerVAST from "./PartnerVAST";
import YoutubeURLForm from "./YoutubeURLForm";
import YoutubeUrlTile from "./YoutubeUrlTile";
import ImposiumMultiCreativeModal from "widgets/modals/imposium/ImposiumMultiCreativeModal";

import { useModalStack, formatBytes, noop, getUploadFiles } from "utils/common";

import useCancelToken from "hooks/useCancelToken";
import SelectAssets from "widgets/modals/assets/SelectAssets";

import './CreateVAST.css';

export default function CreateVAST({
	returnPath,
	libraryId,
	creativeId,
	isMultiple,
}){
	const dispatch = useDispatch();
	const isVastYoutubeEnabled = useFlag(FEATURE_FLAGS.VAST_YOUTUBE);
	let modalTitle = "Create VAST"
		;

	if(creativeId){
		modalTitle = "Select New Video";
	}

	useActiveCreative(creativeId);

	const commonModalProps = {
		title: modalTitle,
		type: "modal",
		className: `create-vast-modal${isVastYoutubeEnabled ? " youtube-url-enabled" : ""}`,
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	// eslint-disable-next-line no-unused-vars
	const [modalStack, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });

	const { isLoaded, advertiserId } = useSelector(state => ({
		isLoaded: state.libraryProperties?.isLoaded,
		advertiserId: state.libraryProperties?.advertiser?.id,
	}));

	let { token: cancelToken, cancel } = useCancelToken()
		, [isUploading, setIsUploading] = useState(false)
		, [dynamicValue, setDynamicValue] = useState(VAST_NON_DYNAMIC)
		, [autoCreateAEVersionRenderAssets, setAutoCreateAEVersionRenderAssets] = useState(false)
		, isAfterEffectsVASTSelected = dynamicValue === VAST_DYNAMIC_AFTER_EFFECTS
		, isImposiumSelected = dynamicValue === VAST_IMPOSIUM
		, isDynamic = (dynamicValue === VAST_DYNAMIC) || isAfterEffectsVASTSelected
		, replaceCreative = false
		;

	const fileTypeAccept = isAfterEffectsVASTSelected ? UPLOAD_VAST_AFTER_EFFECTS_FORMATS : UPLOAD_VAST_ACCEPTED_FORMATS;
	const maxUploadSize = isAfterEffectsVASTSelected ? UPLOAD_AFTER_EFFECTS_MAX_BYTES : UPLOAD_VIDEO_ASSET_MAX_BYTES;
	const formattedMaxSize = formatBytes(maxUploadSize);

	function closeModal(){
		cancel("Uploads canceled.");
		dispatch(push(returnPath));
	}

	// HOPI-524
	if(!isMultiple && creativeId){
		replaceCreative = { id:creativeId };
	}

	function openSelect(){
		function Configured(props){
			const modalProps = {
				...props,
				commonModalProps: {
					...props.commonModalProps,
					title: `${modalTitle} - ${VAST_BUILD_METHOD_LABELS[dynamicValue]}`,
				},
				type: isAfterEffectsVASTSelected ? "afterEffects" : "video",
				multiple: isMultiple && !isImposiumSelected,
				submitButtonText: "Create VAST Creative",
				submitButtonTheme: "green",

				onSelect: (selected) => {
					if (isAfterEffectsVASTSelected) {
						return dispatch(encodeVASTAfterEffectsCreative(libraryId, selected, autoCreateAEVersionRenderAssets))
							.then((response) => {
								closeModal();
							});
					}

					if (isImposiumSelected) {
						return dispatch(encodeVASTImposiumCreative(libraryId, selected))
							.then((response) => {
								closeModal();
							});
					}

					return dispatch(encodeVASTCreatives(libraryId, selected, replaceCreative, dynamicValue))
						.then((response) => {
							closeModal();
						});
				},
				allowEncodedVideo: false,
				accept: fileTypeAccept,
			};
			return <SelectAssets {...modalProps} />;
		}
		pushModal(Configured);
	}

	function openSkip(file, message, onSkip = noop){
		function Configured(props){
			let modalProps = {
				...props,

				file,
				message,
				onSkip,
			};
			return <SkipModal {...modalProps} />;
		}
		pushModal(Configured);
	}

	function openImposiumProjectModal() {
		function Configured(props) {
			const modalProps = {
				...props,
				onClose: closeModal,
			};

			return <ImposiumMultiCreativeModal {...modalProps} />;
		}

		pushModal(Configured);
	}

	/**
	 * Only a single After Effects file can be uploaded at a time so we do not need to handle
	 * the post case for multiple files unlike regular VAST creatives.
	 *
	 * If the response succeeds, then attach a success message.
	 * If the response fails, we should show the AE error modal.
	 *
	 * @param {*} file After Effects Package to be uploaded
	 */
	function uploadAfterEffects(file) {
		if(file.size > maxUploadSize){
			let description = `Cannot upload file “${file.name}” because it exceeds the maximum file size of ${formattedMaxSize}.`;
			openSkip(file, description);
			return;
		}

		return dispatch(uploadVASTAfterEffectsCreative(libraryId, file, cancelToken, autoCreateAEVersionRenderAssets))
			.then((resp) => {
				if (resp.isCanceled) {
					closeModal();
					return;
				}

				if (resp.success) {
					dispatch(addNotification({
						message: encodeVastGrowls(1, isDynamic),
						type: "success",
					}));
					dispatch(loadCreatives(libraryId));
					closeModal();
				} else {
					const { errors = [], filename, status } = resp.payload?.errorObj || {};
					closeModal();

					if (errors.length && status === "FAILED") {
						dispatch(openModal(MODAL_TYPES.AE_UPLOAD_ERRORS, { errors, filename }));
					}
				}
			});
	}


	function onUpload(e){
		let fileList = e.target.files;

		dispatch(setVASTUploadList(getUploadFiles(fileList)));
		setIsUploading(true);
		let numSuccessful = 0;

		function uploadFile(itemNum){
			let file = fileList.item(itemNum);

			if(!file){
				if(numSuccessful > 0) {
					dispatch(addNotification({
						message: encodeVastGrowls(numSuccessful, isDynamic),
						type: "success",
					}));
					dispatch(loadCreatives(libraryId));
				}
				closeModal();
				return;
			}

			if(file.size > maxUploadSize){
				let description = `Cannot upload file “${file.name}” because it exceeds the maximum file size of ${formattedMaxSize}.`;
				openSkip(file, description, () => { uploadFile(itemNum + 1); });
				return;
			}

			dispatch(uploadVASTCreative(libraryId, file, cancelToken, replaceCreative, dynamicValue))
				.then((resp) => {
					if(resp.isCanceled){
						closeModal();
						return;
					}

					if(resp.success){
						/*
							The response is "successful" even when errors are sent back for a file upload.
							We are submitting uploads sequentially, so errors will always be in the 0 index
							as opposed to matching `itemNum`.

							However, even with an error, we only want to skip the creative if it does not exist
							in the creatives payload.
						*/
						const { creatives = [], errors = []} = resp.payload?.data || {};

						if (!creatives.length && errors.length) {
							const [errorObj] = errors;
							const description = errorObj.errors.map(errorObj => errorObj.error).join('');
							openSkip(file, description, () => { uploadFile(itemNum + 1); });
							return;
						}

						numSuccessful += 1;
						uploadFile(itemNum + 1);
					} else {
						let { description = `Cannot upload file ${file.name}` } = (resp?.payload?.errorObj || {});
						openSkip(file, description, () => { uploadFile(itemNum + 1); });
					}
				});
		}

		if (isAfterEffectsVASTSelected) {
			uploadAfterEffects(fileList[0]);
			return;
		}

		if (isImposiumSelected) {
			dispatch(uploadVASTImposiumCreative(libraryId, fileList[0]))
				.then((resp) => {
					closeModal();
				});

			return;
		}

		uploadFile(0);
	}

	function selectPartner(){
		function Configured(props){
			let modalProps = {
				...props,
			};

			return <PartnerVAST {...modalProps} libraryId={libraryId} />;
		}

		pushModal(Configured);
	}

	function openYoutubeForm(){
		function Configured(props){
			let modalProps = {
				...props,
			};

			return <YoutubeURLForm {...modalProps} libraryId={libraryId} />;
		}

		pushModal(Configured);
	}

	function Body() {
		if(isUploading){
			return <VASTUploadList />;
		}

		const headerContent = () => {
			const handleChange = (e) => {
				setDynamicValue(e.target.value);
			};

			return (
				<header className="header-info-vast">
					<strong>Select build method</strong>
					<div className="build-methods">
						<ReduxControls.Checkbox
							name="nonDynamic"
							label={
								<>
									<span>Standard</span>
									<span>Video</span>
								</>
							}
							type="radio"
							value={VAST_NON_DYNAMIC}
							checked={dynamicValue === VAST_NON_DYNAMIC}
							onChange={handleChange}
						/>
						<ReduxControls.Checkbox
							name="dynamic"
							label={
								<>
									<span>Dynamic -</span>
									<span>Ad Studio</span>
								</>
							}
							type="radio"
							value={VAST_DYNAMIC}
							checked={dynamicValue === VAST_DYNAMIC}
							onChange={handleChange}
						/>
						{!!advertiserId && <ReduxControls.Checkbox
							name="imposium"
							className="cypress-imposium-build-method"
							label={VAST_BUILD_METHOD_LABELS[VAST_IMPOSIUM]}
							type="radio"
							value={VAST_IMPOSIUM}
							checked={isImposiumSelected}
							onChange={handleChange}
						/>}
						<ReduxControls.Checkbox
							name="dynamicAfterEffects"
							label={
								<>
									<span>Dynamic -</span>
									<span>After Effects</span>
								</>
							}
							type="radio"
							value={VAST_DYNAMIC_AFTER_EFFECTS}
							checked={isAfterEffectsVASTSelected}
							onChange={handleChange}
						/>
					</div>
				</header>
			);
		};

		let selectionHeader = "Select how you would like to add videos";
		let uploadHeader = "Upload Videos";
		let uploadMessage = `(${formattedMaxSize} max file size per video)`;
		let chooseExistingHeader = "Choose Existing Videos";

		if (isAfterEffectsVASTSelected) {
			uploadHeader = "Upload After Effects Package";
			uploadMessage = `(${formattedMaxSize} max file size)`;
			chooseExistingHeader = "Choose Existing After Effects Package";
		}

		if (isImposiumSelected) {
			selectionHeader = "Select from below to create an Imposium creative";
			uploadMessage = `${formattedMaxSize} max file size`;
		}

		let partnerVAST = null;
		let youtubeUrlTile = null;

		if(dynamicValue === "nonDynamic"){
			// Empty spaces are assigned to message props to appear as an empty string or else error text will appear if message isn't defined
			partnerVAST = (
				<div onClick={selectPartner}>
					<EmptyItem className="vast-tag" icon="fal fa-code" title="Creative Tag(s)" message=" " />
				</div>
			);
			if(isVastYoutubeEnabled){
				youtubeUrlTile = <YoutubeUrlTile onClick={openYoutubeForm} />;
			}
		}

		return (
			<div className="body-container">
				<ProgressIndicator key="loading" isLoading={!isLoaded} />
				{headerContent()}

				<div className="selection-text">
					<strong>{selectionHeader}</strong>
					<p>Accepted file types: {fileTypeAccept}</p>
				</div>
				{isAfterEffectsVASTSelected && (
					<ReduxControls.Checkbox
						name="autoCreateAEVersionRenderAssets"
						className="auto-save-assets-checkbox"
						label="Save version video renders to the asset library"
						checked={autoCreateAEVersionRenderAssets}
						onChange={ev => setAutoCreateAEVersionRenderAssets(ev.target.checked)}
					/>
				)}

				<div className="selection-routes">
					{ isImposiumSelected && <div
						className="cypress-imposium-open-blank-imposium"
						onClick={openImposiumProjectModal}>
						<EmptyItem
							icon="fal fa-file-plus"
							title="Imposium Project"
							message=" "
						/>
					</div> }

					<label htmlFor={UPLOAD_VAST_CREATIVE} className="upload-videos">
						<EmptyItem
							icon="fal fa-cloud-upload"
							title={uploadHeader}
							message={uploadMessage}
						/>
					</label>
					<div className="choose-existing-click-wrapper" onClick={openSelect}>
						<EmptyItem
							className="choose-existing"
							icon="fal fa-folder-open"
							title={chooseExistingHeader}
							message=" "
						/>
					</div>
					{partnerVAST}
					{youtubeUrlTile}
				</div>
				<input
					type="file"
					multiple={isMultiple && !isAfterEffectsVASTSelected && !isImposiumSelected}
					id={UPLOAD_VAST_CREATIVE}
					className="hidden"
					accept={fileTypeAccept}
					onChange={onUpload} />
			</div>
		);
	}

	function Footer() {
		return (
			<Button
				theme="default"
				type="reset"
				onClick = { closeModal }
			>Cancel</Button>
		);
	}

	if(ModalStackHead){
		return <ModalStackHead />;
	}

	return (
		<Modal
			{...commonModalProps}
			renderContent = { Body }
			renderFooter = { Footer }
		/>
	);
}

CreateVAST.displayName = "CreateVAST";
CreateVAST.defaultProps = {
	libraryId:-1,
	creativeId:null,
	returnPath:"",
};
